import type {
	SRT_Row,
	SRT_SortingState,
} from '@/shared/components/master-table'
import type { AnchorResponseModel } from '@/shared/generated'
import type { Updater } from '@tanstack/react-table'

import { Fragment } from 'react/jsx-runtime'
import { useTranslation } from 'react-i18next'

import { MasterTable } from '@/shared/components/master-table'
import NoData from '@/shared/components/placeholders/NoData'
import { Button } from '@/shared/components/ui/button'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { useDatasheetTable } from '@/shared/lib/datasheet/lib/useDatasheetTable'
import { useAnchorsTableConfigStore } from '@/shared/lib/table-config'

import { getTableRowId } from '../../lib/helpers'
import { useColumns } from '../../lib/useColumns'
import { useAnchorsManagementContext } from '../AnchorManagementProvider'
import { AnchorAssortmentTableToolbar } from './AnchorAssortmentTableToolbar'

export default function AnchorsAssortmentTable() {
	const { t } = useTranslation()
	const [{ filters, selected, sorting }, setQuery] = useLocationQuery()
	const { anchors, isFetching, handleImportReportClick } =
		useAnchorsManagementContext()

	const getRowClassName = ({ row }: { row: SRT_Row<AnchorResponseModel> }) =>
		getTableRowId(row.original) === selected
			? 'bg-primary-50 dark:bg-primary-950'
			: ''
	const columns = useColumns()
	const table = useDatasheetTable({
		enableRowSelection: true,
		enableSelectAll: true,
		enableMultiRowSelection: true,
		enableColumnOrdering: true,
		onSaveConfig: (id, config) => useAnchorsTableConfigStore.setState(config),
		getRowClassName,
		getRowId: (row) => {
			if ('srt-row-spacer' in row) return null

			return getTableRowId(row)
		},
		sorting,
		onSortingChange: (updater: Updater<SRT_SortingState>) => {
			setQuery({
				sorting: (typeof updater === 'function'
					? updater(sorting)
					: updater) as SRT_SortingState,
			})
		},
		data: anchors?.data || [],
		columns,
		isLoading: isFetching,
	})
	const isNoData =
		!isFetching && !filters.length && !anchors?.data?.length && !anchors?.total

	return (
		<Fragment>
			<AnchorAssortmentTableToolbar table={table} />
			<div className='border-base relative flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
				<MasterTable table={table} />
				{isNoData && (
					<div className='absolute inset-0 flex items-center justify-center'>
						<NoData
							className='flex-col pt-10'
							title={t('anchors.table.empty.title')}
							subtitle={t('anchors.table.empty.subtitle')}
						>
							<Button onClick={handleImportReportClick} variant='primary'>
								{t('app.import_from_file')}
							</Button>
						</NoData>
					</div>
				)}
			</div>
		</Fragment>
	)
}
